import React from 'react';
import {
  Box, Card, CardContent, Typography,
  Tooltip as MuiTooltip, // recharts 도 Tooltip 을 내보내 이름이 겹친다 - MainDashboardPage 와 같은 회피법.
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { PieChart, Pie, Cell } from 'recharts';
import { PQCD } from '../../lib/pqcdMetrics';

// 게이지는 ResponsiveContainer 를 쓰지 않고 고정 픽셀로 그린다. 기준값(reference) 눈금을
// 그리려면 중심(cx,cy)과 반지름을 미리 알아야 하는데, ResponsiveContainer 는 실제로 그려질
// 때까지 픽셀 크기를 몰라 눈금 좌표를 미리 계산할 수 없다. 카드 하나 폭(휴대폰 가로폭
// 400px 기준으로도 넉넉하다)이 이보다 좁아질 일은 없다고 보고 고정폭으로 그린다.
const CHART_W = 200;
const CHART_H = 118;
const CX = CHART_W / 2;
const CY = 108; // 중심을 바닥 쪽에 둬서 위쪽 반원만 보이게 한다.
const OUTER_R = 92;
const INNER_R = 68;

const RADIAN = Math.PI / 180;

// recharts Pie 의 각도 관례: 0°=3시 방향, 90°=12시 방향, 180°=9시 방향(시계 반대 방향으로 증가).
// startAngle=180 → endAngle=0 로 그리면 9시에서 시작해 12시를 지나 3시에서 끝나는 위쪽 반원이 된다.
const polarToCartesian = (angleDeg, radius) => ({
  x: CX + radius * Math.cos(RADIAN * angleDeg),
  y: CY - radius * Math.sin(RADIAN * angleDeg),
});

const GOOD_COLOR = '#2e7d32';
const BAD_COLOR = '#d32f2f';
const NEUTRAL_COLOR = '#9e9e9e';
const TRACK_COLOR = '#e0e0e0';

/**
 * 지표 설명 말풍선. MainDashboardPage.js 의 MetricHelp 와 완전히 같은 모양(desc/formula/note)을
 * 받아 같은 방식으로 보여준다. 그 컴포넌트는 export 되어 있지 않고(로컬 함수) 이 파일이
 * MainDashboardPage 를 import 하면 순환·결합이 생기므로, 최소한만 그대로 복제해 둔다 -
 * 문구를 바꿀 일이 있으면 두 곳 다 봐야 한다는 뜻이니 유의.
 */
const GaugeHelp = ({ help }) => {
  if (!help) return null;
  const cat = PQCD[help.category];
  return (
    <MuiTooltip
      arrow
      placement="top"
      enterTouchDelay={0}
      leaveTouchDelay={6000}
      title={
        <Box sx={{ py: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'inherit' }}>
            [{cat.key}] {cat.label} · {help.title}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>
            {help.desc}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.85 }}>
            계산식 : {help.formula}
          </Typography>
          {help.note && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.85 }}>
              {help.note}
            </Typography>
          )}
        </Box>
      }
    >
      <HelpOutlineIcon
        sx={{
          fontSize: 14, ml: 0.5, verticalAlign: 'middle',
          color: 'text.disabled', cursor: 'help',
          '&:hover': { color: 'text.secondary' },
        }}
      />
    </MuiTooltip>
  );
};

/**
 * 반원(180°→0°) 변화율 게이지 카드.
 *
 * recharts 에 반원 게이지 전용 차트가 없어 Pie 두 장을 겹쳐서 만든다.
 *   1) 배경 트랙 - 전체 반원을 옅은 회색으로 채운 Pie 한 장(항상 100%).
 *   2) 값 아크 - [채워진 만큼, 나머지] 두 조각짜리 Pie. "나머지" 조각은 fill="transparent" 로
 *      비워둬서 그 밑의 트랙이 그대로 비쳐 보이게 한다 - 그래서 항상 트랙 위에 그려야 한다.
 * 기준값(reference) 눈금은 recharts Pie 의 자식이 아니라 같은 자리에 겹친 별도 <svg> 로
 * 그린다 - Pie/Cell 이 아닌 임의의 SVG 태그를 차트 내부 자식으로 넣으면 recharts 가
 * 인식하지 못해 그리지 않을 수 있기 때문이다(차트 종류별로 정해진 자식만 렌더링).
 *
 * 값이 스케일(min~max)을 벗어나도(예: 시간당 생산량이 기준의 250%) 아크는 max/min 에서
 * clamp 하지만 중앙 숫자는 실제 값을 그대로 보여준다 - 게이지 모양은 깨지지 않게 하면서도
 * 숫자를 속이지 않기 위함이다.
 */
const PqcdGauge = ({
  title, category, value, unit = '%', min, max, reference,
  higherIsBetter = true, lines = [], help,
}) => {
  const cat = PQCD[category];
  const hasValue = value != null && !Number.isNaN(Number(value));
  const numericValue = hasValue ? Number(value) : null;

  const range = max - min;
  const clamped = hasValue ? Math.min(max, Math.max(min, numericValue)) : min;
  const fraction = (clamped - min) / range; // 0~1, 아크가 채워질 비율
  const refFraction = (Math.min(max, Math.max(min, reference)) - min) / range;

  // 색상: reference(변화 없음 지점) 대비 좋아졌는지/나빠졌는지로만 정한다.
  // higherIsBetter 로 방향을 뒤집는다 - 불량률/재공재고처럼 낮을수록 좋은 지표는
  // reference 보다 작을 때 초록색이 되어야 한다.
  let arcColor = NEUTRAL_COLOR;
  if (hasValue && numericValue !== reference) {
    const better = higherIsBetter ? numericValue > reference : numericValue < reference;
    arcColor = better ? GOOD_COLOR : BAD_COLOR;
  }

  const arcData = [{ value: fraction }, { value: 1 - fraction }];
  const arcColors = [arcColor, 'transparent'];

  const refAngle = 180 - refFraction * 180;
  const tickInner = polarToCartesian(refAngle, INNER_R - 6);
  const tickOuter = polarToCartesian(refAngle, OUTER_R + 6);

  return (
    // 위쪽 4px 테두리는 category 색(어느 항목인지) - 아크는 그대로 상태색(좋음/나쁨)이다.
    <Card variant="outlined" sx={{ height: '100%', borderTop: `4px solid ${cat.color}` }}>
      <CardContent sx={{ textAlign: 'center', pb: '12px !important' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
          {/* 항목 배지 - 카드 아이콘과 같은 category 색이라 위 카드와 색으로 짝지어 보인다. */}
          <Box
            component="span"
            sx={{
              display: 'inline-block', mr: 0.75, px: 0.9, borderRadius: 2,
              bgcolor: cat.color, color: '#fff', fontWeight: 800, lineHeight: 1.6,
            }}
          >
            {cat.key}
          </Box>
          {title}
          <GaugeHelp help={help} />
        </Typography>

        <Box sx={{ position: 'relative', width: CHART_W, height: CHART_H, mx: 'auto', mt: 0.5 }}>
          <PieChart width={CHART_W} height={CHART_H}>
            <Pie
              data={[{ value: 1 }]} dataKey="value"
              cx={CX} cy={CY} startAngle={180} endAngle={0}
              innerRadius={INNER_R} outerRadius={OUTER_R}
              isAnimationActive={false} stroke="none"
            >
              <Cell fill={TRACK_COLOR} />
            </Pie>
            <Pie
              data={arcData} dataKey="value"
              cx={CX} cy={CY} startAngle={180} endAngle={0}
              innerRadius={INNER_R} outerRadius={OUTER_R}
              isAnimationActive={false} stroke="none"
            >
              {arcData.map((entry, i) => (
                <Cell key={`arc-${i}`} fill={arcColors[i]} />
              ))}
            </Pie>
          </PieChart>

          {/* 기준값 눈금 - PieChart 위에 같은 크기로 겹친 별도 svg. */}
          <svg
            width={CHART_W} height={CHART_H}
            style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
          >
            <line
              x1={tickInner.x} y1={tickInner.y} x2={tickOuter.x} y2={tickOuter.y}
              stroke="#616161" strokeWidth={2}
            />
          </svg>

          <Box sx={{
            position: 'absolute', left: 0, right: 0, bottom: 4,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: hasValue ? arcColor : NEUTRAL_COLOR, lineHeight: 1 }}>
              {hasValue ? numericValue.toFixed(1) : '-'}
              {hasValue && (
                <Typography component="span" variant="caption" sx={{ ml: 0.3, color: 'text.secondary' }}>
                  {unit}
                </Typography>
              )}
            </Typography>
          </Box>
        </Box>

        {lines.map((line, i) => (
          <Typography
            key={i}
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: i === 0 ? 0.5 : 0 }}
          >
            {line}
          </Typography>
        ))}
      </CardContent>
    </Card>
  );
};

export default PqcdGauge;
